import React, { useEffect, useState } from 'react'
import { Image, Linking, useWindowDimensions, Platform } from 'react-native'
import { ArrowLeft, MapPin, Camera, Store, Clock } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button, ButtonText, Card, EmptyState, FavoriteButton, ListItem, Spinner, Text,
  XStack, YStack, ZStack, ScrollView, ScreenLayout,
} from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'ProductView'>
  route: RouteProp<RootStackParamList, 'ProductView'>
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  description: string | null
  description_en: string | null
  description_fi: string | null
  price: number
  status: string
  created_at: string
  barcode: string | null
  flea_market: {
    id: string
    name: string
    address_street: string | null
    address_city: string | null
    logo_url: string | null
  } | null
  product_images: { id: string; image_url: string }[]
  user: { id: string; display_name: string | null } | null
}

const MAX_CONTENT_WIDTH = 768
const MAX_IMAGE_WIDTH = 480
const DESKTOP_BREAKPOINT = 768

export default function ProductViewScreen({ navigation, route }: Props) {
  const { productId } = route.params
  const { user } = useAuth()
  const { t, language } = useLanguage()
  const { width: screenWidth } = useWindowDimensions()
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isFavorited, setIsFavorited] = useState(false)
  const [favoriteRowId, setFavoriteRowId] = useState<string | null>(null)

  const isDesktop = screenWidth >= DESKTOP_BREAKPOINT
  const CARD_MARGIN = 16
  const imageWidth = isDesktop
    ? MAX_IMAGE_WIDTH
    : Math.min(screenWidth, MAX_CONTENT_WIDTH) - CARD_MARGIN * 2
  const imageHeight = Math.round(imageWidth * 0.75) // 4:3

  useEffect(() => {
    fetchProduct()
    fetchFavoriteStatus()
  }, [productId, user?.id])

  const fetchFavoriteStatus = async () => {
    if (!user?.id) return
    try {
      const { data } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', productId)
        .maybeSingle()
      if (data) {
        setIsFavorited(true)
        setFavoriteRowId(data.id)
      }
    } catch (e) {
      console.error('Error checking favorite:', e)
    }
  }

  const toggleFavorite = async () => {
    if (!user?.id) { navigation.navigate('Login'); return }
    const wasFavorited = isFavorited
    setIsFavorited(!wasFavorited)
    try {
      if (wasFavorited && favoriteRowId) {
        await supabase.from('favorites').delete().eq('id', favoriteRowId)
        setFavoriteRowId(null)
      } else {
        const { data, error } = await supabase
          .from('favorites')
          .insert({ user_id: user.id, product_id: productId })
          .select('id')
          .single()
        if (error) throw error
        setFavoriteRowId(data.id)
      }
    } catch (e) {
      console.error('Error toggling favorite:', e)
      setIsFavorited(wasFavorited)
    }
  }

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id, title, title_en, title_fi,
          description, description_en, description_fi,
          price, status, created_at, barcode,
          flea_market:flea_markets ( id, name, address_street, address_city, logo_url ),
          product_images ( id, image_url ),
          user:users ( id, display_name )
        `)
        .eq('id', productId)
        .single()
      if (error) throw error
      setProduct({
        ...data,
        flea_market: Array.isArray(data.flea_market) ? data.flea_market[0] : data.flea_market,
        user: Array.isArray(data.user) ? data.user[0] : data.user,
      } as Product)
    } catch (error) {
      console.error('Error fetching product:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = () => {
    if (!product) return ''
    if (product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || product.title_en || product.title_fi || 'Nimetön tuote'
  }

  const getDescription = () => {
    if (!product) return ''
    if (product.description_fi) return product.description_fi
    if (language === 'en' && product.description_en) return product.description_en
    return product.description || product.description_en || product.description_fi || ''
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('fi-FI', { year: 'numeric', month: 'long', day: 'numeric' })

  const openMaps = () => {
    if (!product?.flea_market) return
    const address = [product.flea_market.address_street, product.flea_market.address_city].filter(Boolean).join(', ')
    if (address) Linking.openURL(`https://maps.google.com/?q=${encodeURIComponent(address)}`)
  }

  const handleStorePress = () => {
    if (product?.flea_market) {
      navigation.navigate('StoreBrowse', { marketId: product.flea_market.id, marketName: product.flea_market.name })
    }
  }

  if (loading) {
    return (
      <YStack f={1} bg="$background" jc="center" ai="center">
        <Spinner size="small" />
      </YStack>
    )
  }

  if (!product) {
    return (
      <YStack f={1} bg="$background" jc="center" ai="center">
        <EmptyState
          icon={<Camera size={48} color="#858B80" />}
          title="Tuotetta ei löytynyt"
          action={<Button variant="primary" onPress={() => navigation.goBack()}><ButtonText>Takaisin</ButtonText></Button>}
        />
      </YStack>
    )
  }

  const images = product.product_images || []
  const storeAddress = product.flea_market
    ? [product.flea_market.address_street, product.flea_market.address_city].filter(Boolean).join(', ')
    : ''

  return (
    <YStack f={1} bg="$background">
      {/* Top bar — in normal flow, not absolute */}
      <XStack
        jc="space-between" ai="center" px="$md"
        pt={Platform.OS === 'web' ? 12 : 52} pb={8}
        maxWidth={MAX_CONTENT_WIDTH} alignSelf="center" width="100%"
      >
        <YStack
          w={40} h={40} br="$lg" bg="$backgroundCard" jc="center" ai="center"
          onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }}
          animation="quick" cursor="pointer"
        >
          <ArrowLeft size={20} color="#3E423A" />
        </YStack>
        <YStack w={40} h={40} br="$lg" bg="$backgroundCard" jc="center" ai="center">
          <FavoriteButton active={isFavorited} onPress={toggleFavorite} size={22} />
        </YStack>
      </XStack>

      <ScrollView
        contentContainerStyle={{ maxWidth: MAX_CONTENT_WIDTH, alignSelf: 'center', width: '100%' }}
        showsVerticalScrollIndicator={false}
      >

        {/* Image gallery */}
        <YStack mx="$md" br="$xl" overflow="hidden" bg="$backgroundSecondary">
          <YStack
            bg="$backgroundSecondary" w={imageWidth} h={imageHeight} position="relative"
          >
            {images.length > 0 ? (
              <>
                <ScrollView
                  horizontal pagingEnabled showsHorizontalScrollIndicator={false}
                  onMomentumScrollEnd={(e: any) => {
                    setCurrentImageIndex(Math.round(e.nativeEvent.contentOffset.x / imageWidth))
                  }}
                >
                  {images.map((img) => (
                    <Image key={img.id} source={{ uri: img.image_url }} style={{ width: imageWidth, height: imageHeight }} resizeMode="cover" />
                  ))}
                </ScrollView>
                {images.length > 1 && (
                  <XStack position="absolute" bottom={16} left={0} right={0} jc="center" gap="$sm">
                    {images.map((_, i) => (
                      <YStack
                        key={i} h={8} br={4}
                        bg={i === currentImageIndex ? '#fff' : 'rgba(255,255,255,0.45)'}
                        w={i === currentImageIndex ? 20 : 8}
                      />
                    ))}
                  </XStack>
                )}
              </>
            ) : (
              <YStack f={1} jc="center" ai="center" gap="$sm">
                <Camera size={48} color="$colorSecondary" />
                <Text color="$colorSecondary" fontSize={14}>Ei kuvaa</Text>
              </YStack>
            )}
          </YStack>
        </YStack>

        {/* Price + Title card */}
        <YStack
          bg="$backgroundCard" mx="$md" mt="$md" br={20} p="$lg"
          {...(Platform.OS === 'web'
            ? { style: { boxShadow: '0 2px 12px rgba(0,0,0,0.08)' } as any }
            : { elevation: 3 })}
        >
          <Text fontSize={28} fontWeight="700" color="$colorPrimary" mb={6}>{product.price?.toFixed(2)} €</Text>
          <Text fontSize={20} fontFamily="$cursive" color="$color" lineHeight={26} mb="$md">
            {getTitle()}
          </Text>
          <XStack ai="center" gap="$sm">
            <Clock size={14} color="$colorSecondary" />
            <Text fontSize={13} color="$colorSecondary">Lisätty {formatDate(product.created_at)}</Text>
          </XStack>
          {product.status === 'for_sale' && (
            <XStack ai="center" gap="$sm" mt="$md" bg="$colorPrimaryMuted" als="flex-start" px="$md" py="$sm" br={20}>
              <YStack w={8} h={8} br={4} bg="$colorPrimary" />
              <Text fontSize={13} fontWeight="600" color="$colorPrimary">Myynnissä</Text>
            </XStack>
          )}
        </YStack>

        {/* Description */}
        {getDescription() ? (
          <Card mx="$md" mt="$md" p="$lg" br="$xl">
            <Text fontSize={12} fontWeight="600" color="$colorSecondary" textTransform="uppercase" letterSpacing={0.8} mb={10}>
              Kuvaus
            </Text>
            <Text fontSize={15} color="$color" lineHeight={22}>{getDescription()}</Text>
          </Card>
        ) : null}

        {/* Store card */}
        {product.flea_market && (
          <ListItem
            icon={
              product.flea_market.logo_url ? (
                <Image
                  source={{ uri: product.flea_market.logo_url }}
                  style={{ width: 48, height: 48, borderRadius: 14 }}
                  resizeMode="cover"
                />
              ) : (
                <YStack w={48} h={48} br={14} bg="$colorPrimaryMuted" jc="center" ai="center">
                  <Store size={22} color="$colorPrimary" />
                </YStack>
              )
            }
            title={product.flea_market.name}
            subtitle={storeAddress ? `${storeAddress}\nSelaa kaupan tuotteita` : 'Selaa kaupan tuotteita'}
            onPress={handleStorePress}
            mx={16} mt={12} bg="$backgroundCard" br="$xl" p="$md"
          />
        )}

        {/* Directions */}
        {product.flea_market && storeAddress ? (
          <Button variant="primary" mx={16} mt={12} br="$lg" onPress={openMaps}>
            <MapPin size={18} color="#fff" />
            <ButtonText ml="$xs">Näytä kartalla</ButtonText>
          </Button>
        ) : null}



        <YStack h={32} />
      </ScrollView>
    </YStack>
  )
}
